import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { stytch } from '../config/stytch'
import { useAuth } from '../context/AuthContext'
import authService from '../services/authService'

const AuthCallback = () => {
  const navigate = useNavigate()
  const { setUser, getUserAppId } = useAuth()
  const [error, setError] = useState(null)

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Get the token from URL parameters
        const urlParams = new URLSearchParams(window.location.search)

        // Stytch uses 'token' parameter for OAuth callbacks
        let token = urlParams.get('token')

        // Also check for stytch_token as fallback
        if (!token) {
          token = urlParams.get('stytch_token')
        }

        if (!token || typeof token !== 'string' || token.trim() === '') {
          throw new Error('No authentication token found in URL')
        }

        // Authenticate with Stytch using the token
        // Note: vanilla-js SDK expects token as first parameter, options as second
        const response = await stytch.oauth.authenticate(token, {
          session_duration_minutes: 60 * 24 * 365, // 1 year
        })

        console.log('Authentication successful:', response)

        // Store JWT and register with backend (try login first, fallback to signup)
        await authService.handleOAuthSuccess(response)

        // Update user state
        if (response.user) {
          setUser(response.user)

          // Get app-specific user ID
          await getUserAppId()

          navigate('/dashboard')
        }
      } catch (error) {
        console.error('OAuth callback error:', error)
        console.error('Error details:', {
          message: error.message,
          error_message: error.error_message,
          error_type: error.error_type,
        })

        const errorMessage = error.error_message || error.message || 'Authentication failed'
        setError(errorMessage)

        // Redirect to home after showing error
        setTimeout(() => {
          navigate('/')
        }, 3000)
      }
    }

    handleOAuthCallback()
  }, [navigate, setUser])

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          px: 3,
        }}
      >
        <Typography variant="h5" color="error" sx={{ fontWeight: 600 }}>
          Authentication Failed
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 500 }}>
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirecting to home...
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 3,
      }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" color="text.secondary">
        Completing authentication...
      </Typography>
    </Box>
  )
}

export default AuthCallback
